import { Link } from "react-router-dom";
import { ArrowRight, Building2, Heart, Users, Award, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const AboutPage = () => {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              About Us
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              A Legacy of <span className="text-amber-700 italic">Advocacy</span> and Care
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              For over a decade, the Sickle Cell Association has been at the forefront of patient advocacy, policy reform, and community support for those affected by sickle cell disease.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1631558554184-319c88f4f8a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwQW1lcmljYW4lMjBkb2N0b3IlMjB0YWxraW5nJTIwdG8lMjBwYXRpZW50JTIwY29tcGFzc2lvbmF0ZXxlbnwwfHx8fDE3NzAxNDM5NzR8MA&ixlib=rb-4.1.0&q=85"
                alt="Our compassionate care"
                className="w-full h-[500px] object-cover rounded-sm shadow-lg" />

            </div>
            <div>
              <h2 className="font-serif text-4xl font-semibold text-slate-900 mb-6">
                Our Story
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                The Sickle Cell Association was founded with a singular mission: to improve the lives of individuals and families affected by sickle cell disease in the Greater Houston area and beyond.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                What began as a grassroots effort has grown into one of the most influential sickle cell advocacy organizations across the country. Through tireless dedication and strategic partnerships, we have achieved landmark legislative victories that have improved care for thousands of Texans.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Today, we continue our work as a partner agency for the City of Houston and the Houston Health Department, bringing the voice of the sickle cell community to the highest levels of government.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-slate-50" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-l-4 border-l-amber-600 shadow-lg">
              <CardContent className="p-10">
                <h3 className="font-serif text-3xl font-semibold text-slate-900 mb-4">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  To advocate for and support individuals affected by sickle cell disease through policy reform, patient education, community outreach, and strategic partnerships that improve quality of life and healthcare access.
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-teal-600 shadow-lg">
              <CardContent className="p-10">
                <h3 className="font-serif text-3xl font-semibold text-slate-900 mb-4">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed">
                  A future where every person with sickle cell disease has access to comprehensive care, lives free from pain crises, and is supported by a community that understands and advocates for their needs.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-24 bg-white" data-testid="partnerships-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Our Partnerships
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900">
              Trusted Government Partners
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="card-hover border-slate-100 shadow-[0_2px_40px_-12px_rgba(0,0,0,0.08)]">
              <CardContent className="p-10 text-center">
                <Building2 className="w-16 h-16 text-teal-700 mx-auto mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">City of Houston</h3>
                <p className="text-slate-600 leading-relaxed">
                  As an official partner agency for the City of Houston, we work directly with city officials to implement programs and policies that benefit the sickle cell community.
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-slate-100 shadow-[0_2px_40px_-12px_rgba(0,0,0,0.08)]">
              <CardContent className="p-10 text-center">
                <Heart className="w-16 h-16 text-amber-700 mx-auto mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">Houston Health Department</h3>
                <p className="text-slate-600 leading-relaxed">
                  Our partnership with the Houston Health Department enables us to advance public health initiatives and improve healthcare access for sickle cell patients across the city.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Leadership Acknowledgments */}
      <section className="py-24 bg-slate-900" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-amber-400 font-medium tracking-widest uppercase text-sm mb-4">
              Leadership Acknowledgments
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white">
              Champions of Our Cause
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Stephen Williams */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-10">
                <Quote className="w-10 h-10 text-amber-500 mb-6" />
                <p className="text-slate-300 leading-relaxed italic mb-6">
                  "We extend our deepest gratitude to Stephen Williams for his unwavering support and commitment to advancing sickle cell disease initiatives during his tenure."
                </p>
                <div className="border-t border-slate-700 pt-6">
                  <h4 className="font-serif text-xl font-semibold text-white">Stephen Williams</h4>
                  <p className="text-amber-400 text-sm">Former Director, Houston Health Department</p>
                  <p className="text-slate-400 text-sm mt-2">
                    Under his leadership, we forged a partnership that brought unprecedented attention and resources to sickle cell care in Houston.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Jarvis Johnson */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-10">
                <Quote className="w-10 h-10 text-amber-500 mb-6" />
                <p className="text-slate-300 leading-relaxed italic mb-6">
                  "Representative Jarvis Johnson has been a tireless champion for our community, leading legislative efforts that have changed lives across Texas."
                </p>
                <div className="border-t border-slate-700 pt-6">
                  <h4 className="font-serif text-xl font-semibold text-white">Representative Jarvis Johnson</h4>
                  <p className="text-amber-400 text-sm">Texas State Representative</p>
                  <p className="text-slate-400 text-sm mt-2">
                    Championing HB 181 and other critical legislation to establish comprehensive sickle cell support systems statewide.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Senator Borris Miles */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-10">
                <Quote className="w-10 h-10 text-amber-500 mb-6" />
                <p className="!italic mb-6 text-slate-300">"Senator Borris Miles brings a deeply personal perspective to sickle cell advocacy, while fighting for better care for all Texans."

                </p>
                <div className="border-t border-slate-700 pt-6">
                  <h4 className="font-serif text-xl font-semibold text-white">Senator Borris Miles</h4>
                  <p className="text-amber-400 text-sm">Texas State Senator, District 13</p>
                  <p className="!text-sm mt-2 text-slate-400">As a member of the Senate Health and Human Services Committee and a warrior himself, Senator Miles champions SB 820 and critical healthcare legislation for the sickle cell community.

                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Rep Lauren Simmons */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-10">
                <Quote className="w-10 h-10 text-amber-500 mb-6" />
                <p className="text-slate-300 leading-relaxed italic mb-6">
                  "Representative Lauren Simmons fights for sickle cell families with the passion of a mother who knows firsthand the challenges they face every day."
                </p>
                <div className="border-t border-slate-700 pt-6">
                  <h4 className="font-serif text-xl font-semibold text-white">Representative Lauren Simmons</h4>
                  <p className="text-amber-400 text-sm">Texas State Representative, District 146</p>
                  <p className="!text-sm mt-2 text-slate-400">We worked directly with Rep Simmons to push the pendulum of progress for HB 107 to establish the Texas Sickle Cell Disease Registry. As a mother of a daughter with sickle cell disease, Rep. Simmons is dedicated to ensuring Texas takes meaningful action for affected families.

                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 bg-white" data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              What We Do
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900">
              Our Areas of Focus
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="card-hover border-slate-100">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                  <Award className="w-7 h-7 text-amber-700" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">Policy Advocacy</h3>
                <p className="text-slate-600 leading-relaxed">
                  Working with legislators to pass laws that improve care, protect patients, and increase funding for sickle cell programs.
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-slate-100">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-teal-700" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">Patient Support</h3>
                <p className="text-slate-600 leading-relaxed">
                  Providing resources, education, and support services to patients and families navigating life with sickle cell disease.
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-slate-100">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                  <Heart className="w-7 h-7 text-slate-700" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">Community Outreach</h3>
                <p className="text-slate-600 leading-relaxed">
                  Raising awareness, educating the public, and building a supportive community for those affected by sickle cell disease.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl font-semibold text-slate-900 mb-6">
            Join Us in Making a Difference
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed mb-8">
            Whether through membership, volunteering, or donations, your support helps us continue our vital work for the sickle cell community.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/members">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 font-serif italic text-lg btn-primary">
                Become a Member
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-slate-900 text-slate-900 px-8 py-6 font-sans font-bold uppercase text-sm hover:bg-slate-50">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default AboutPage;